import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

import api from '../lib/api';

const AuthContext = createContext(null);

/**
 * Decode JWT payload and check if token is expired.
 * Returns true if the token is valid (not expired), false otherwise.
 */
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return true; // No expiry claim, assume valid
    // Allow 30 second buffer before actual expiry
    return payload.exp * 1000 > Date.now() + 30000;
  } catch {
    return false;
  }
};

const getStoredToken = () => {
  // localStorage token is kept as a UI session hint for page refreshes.
  // The actual auth is handled by httpOnly cookies sent automatically.
  const token = localStorage.getItem('rostafy_token');
  if (token && !isTokenValid(token)) {
    localStorage.removeItem('rostafy_token');
    return null;
  }
  return token;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(getStoredToken);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('rostafy_token');
    setToken(null);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    if (!token || !isTokenValid(token)) {
      if (token) clearAuth();
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, [token, clearAuth]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password, totpCode = null, backupCode = null) => {
    const response = await api.post('/auth/login', {
      email,
      password,
      totp_code: totpCode,
      backup_code: backupCode
    });

    if (response.data.requires_2fa) {
      return { requires_2fa: true };
    }

    localStorage.setItem('rostafy_token', response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return {
      success: true,
      user: response.data.user,
      must_change_password: !!response.data.user?.must_change_password,
      must_setup_2fa: !!response.data.user?.must_setup_2fa
    };
  };

  const register = async (data) => {
    const response = await api.post('/auth/register', data);
    localStorage.setItem('rostafy_token', response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data.user;
  };

  const logout = () => {
    clearAuth();
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser: fetchUser,
    mustChangePassword: !!user?.must_change_password,
    mustSetup2FA: !!user?.must_setup_2fa,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isOrgAdmin: user?.role === 'org_admin' || user?.role === 'super_admin',
    isWorker: user?.role === 'worker'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
